import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";
import { BETA_MODE, BANNER_HEIGHT_PX } from "@/components/BetaBanner";
import {
  Home, Heart, Star, Users, Crown, Sparkles, MessageCircle,
  BookOpen, Flame, TreeDeciduous, Gamepad2, TrendingUp, BarChart3,
  User, GraduationCap, Hash, Sun, Telescope, Apple, Gem, LogOut, ChevronLeft, ChevronRight,
  ShieldAlert, ShieldCheck, Hand, HelpCircle, CalendarDays, Bell, ChevronDown, ChevronUp,
  CircleDollarSign, ClipboardCheck, FileText, LayoutDashboard, Megaphone, MessageSquare, LockKeyhole
} from "lucide-react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  ADMIN_SECTIONS,
  getAccountRoleLabel,
  getAdminControl,
  canAccessAdminSection,
  isActiveAdminSection,
  isAdminRoute,
} from "@/lib/adminAccess";
import { getExploreSections, isExploreFeatureIncludedInPlan, type ExplorePlanTier } from "@workspace/manual";
import { useOrganizationCoverage } from "@/hooks/useSubscription";
import { getEffectiveTier } from "@/lib/subscription";
import { useUsageSummary } from "@/components/UsageMeterCard";
import BrandLogo from "@/components/BrandLogo";

interface NavItem {
  id?: string;
  path: string;
  labelKey: string;
  fallback: string;
  icon: any;
  minimumTier?: ExplorePlanTier;
}

interface NavSection {
  headingKey?: string;
  headingFallback?: string;
  items: NavItem[];
}

const ADMIN_SECTION_ICONS = {
  "": LayoutDashboard,
  analytics: BarChart3,
  applications: ClipboardCheck,
  payouts: CircleDollarSign,
  abuse: ShieldAlert,
  bookings: CalendarDays,
  users: Users,
  payments: CircleDollarSign,
  notifications: Megaphone,
  feedback: MessageSquare,
  backlinks: Telescope,
  audit: FileText,
  vastu: TreeDeciduous,
  "vastu-operations": ShieldCheck,
  access: ShieldCheck,
} as const;

const EXPLORE_ICONS: Record<string, any> = {
  user: User,
  grid: Star,
  heart: Heart,
  users: Users,
  search: Sparkles,
  sun: Sun,
  calendar: CalendarDays,
  sunrise: Sun,
  clock: CalendarDays,
  bell: Bell,
  shield: ShieldAlert,
  hexagon: Gem,
  hash: Hash,
  layers: Star,
  aperture: Hand,
  image: User,
  "message-circle": MessageCircle,
  home: TreeDeciduous,
  "edit-3": BookOpen,
  "trending-up": TrendingUp,
  coffee: Apple,
  briefcase: Users,
  "book-open": BookOpen,
  "check-circle": BarChart3,
  award: Gamepad2,
  telescope: Telescope,
  zap: Crown,
  star: Star,
};

export const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { path: "/home", labelKey: "nav.home", fallback: "Home", icon: Home },
    ],
  },
  ...getExploreSections("web").map((section) => ({
    headingKey: `nav.section.${section.id}`,
    headingFallback: section.title,
    items: section.items.map((feature) => ({
      path: feature.path,
      labelKey: feature.labelKey,
      fallback: feature.label,
      icon: EXPLORE_ICONS[feature.icon] ?? Sparkles,
      id: feature.id,
      minimumTier: feature.minimumTier,
    })),
  })),
];

// Shared collapse state so the page layout can shrink its left margin in
// sync with the sidebar width (the sidebar itself is position:fixed).
const SidebarCollapseContext = createContext<{
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}>({ collapsed: false, setCollapsed: () => {} });

export function SidebarCollapseProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <SidebarCollapseContext.Provider value={{ collapsed, setCollapsed }}>
      {children}
    </SidebarCollapseContext.Provider>
  );
}

export function useSidebarCollapsed() {
  return useContext(SidebarCollapseContext).collapsed;
}

export default function AppSidebar() {
  const [location] = useLocation();
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { collapsed, setCollapsed } = useContext(SidebarCollapseContext);
  const adminControl = getAdminControl(user);
  const roleLabel = getAccountRoleLabel(user);
  const { organization } = useOrganizationCoverage(!!user);
  const { data: usageSummary } = useUsageSummary(!!user);
  const effectiveTier = usageSummary?.tier;
  const adminRouteOpen = isAdminRoute(location);
  const [adminMenuOpen, setAdminMenuOpen] = useState(adminRouteOpen);

  useEffect(() => {
    if (adminRouteOpen) setAdminMenuOpen(true);
  }, [adminRouteOpen]);

  const handleLogout = async () => {
    try {
      await apiRequest("POST", "/api/auth/logout");
    } catch (e) {}
    queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
    window.location.href = "/landing";
  };

  const bannerOffset = BETA_MODE ? BANNER_HEIGHT_PX : 0;

  return (
    <aside
      className={`hidden md:flex fixed left-0 right-auto z-40 min-w-0 box-border flex-col overflow-x-hidden bg-background border-r border-border/50 transition-[width] duration-300 ${collapsed ? "w-16" : "w-56"}`}
      style={{ top: bannerOffset, height: `calc(100vh - ${bannerOffset}px)` }}
    >
      <div className="flex flex-col gap-2 px-2 py-3 border-b border-border/30">
        {!collapsed ? (
          <>
            <div className="flex items-center justify-between">
              <Link href="/home">
                <div className="flex items-center gap-2 px-2.5 cursor-pointer hover:opacity-80 transition-opacity">
                  <BrandLogo />
                </div>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
            </div>
            {roleLabel && (
              <div className="mx-1.5 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-1.5 text-xs font-semibold text-primary">
                {roleLabel}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Link href="/landing">
              <>
                <img
                  src={`${import.meta.env.BASE_URL}brand/mark-dark-128.png`}
                  alt="ZeAstro"
                  className="hidden dark:block w-7 h-7 cursor-pointer"
                  data-no-translate
                />
                <img
                  src={`${import.meta.env.BASE_URL}brand/mark-light-128.png`}
                  alt=""
                  className="block dark:hidden w-7 h-7 cursor-pointer"
                  data-no-translate
                  aria-hidden="true"
                />
              </>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-foreground"
              onClick={() => setCollapsed(false)}
                aria-label="Expand sidebar"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 overflow-y-auto py-2 px-2 sidebar-scroll">
        {adminControl && (
          <div className="mb-3">
            <button
              type="button"
              onClick={() => {
                if (collapsed) {
                  setCollapsed(false);
                  setAdminMenuOpen(true);
                } else {
                  setAdminMenuOpen((open) => !open);
                }
              }}
              className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                adminRouteOpen
                  ? "bg-primary text-primary-foreground"
                  : "bg-primary/10 text-primary hover:bg-primary/15"
              } ${collapsed ? "justify-center" : ""}`}
              title={collapsed ? adminControl.label : undefined}
              aria-expanded={!collapsed && adminMenuOpen}
              aria-controls="admin-control-center-menu"
              data-testid="nav-admin-control-center"
            >
              <ShieldCheck className="h-4 w-4 flex-shrink-0" />
              {!collapsed && (
                <>
                   <span className="min-w-0 flex-1 break-words text-left text-[13px] leading-tight">
                    {adminControl.label}
                  </span>
                  {adminMenuOpen ? (
                    <ChevronUp className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronDown className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  )}
                </>
              )}
            </button>
            {adminMenuOpen && !collapsed && (
              <div
                id="admin-control-center-menu"
                className="mt-1 ml-2 border-l border-primary/20 pl-2 space-y-0.5"
                data-testid="nav-admin-control-center-menu"
              >
                {ADMIN_SECTIONS.filter((section) => canAccessAdminSection(user, section)).map((section) => {
                  const Icon = ADMIN_SECTION_ICONS[section.slug];
                  const active = isActiveAdminSection(location, section);
                  return (
                    <Link key={section.path} href={section.path}>
                      <span
                        className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-xs cursor-pointer transition-colors ${
                          active
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                        }`}
                        aria-current={active ? "page" : undefined}
                      >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                         <span className="min-w-0 break-words">{section.label}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {NAV_SECTIONS.map((section, sIdx) => {
          const heading = section.headingKey ? t(section.headingKey, section.headingFallback) : "";
          return (
            <div key={section.headingKey || `top-${sIdx}`} className={heading ? "mt-4 first:mt-0" : ""}>
              {heading && !collapsed && (
                <div className="px-2.5 pt-1 pb-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
                    {heading}
                  </span>
                </div>
              )}
              {heading && collapsed && (
                <div className="mx-auto my-2 w-6 border-t border-border/30" />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location === item.path || (item.path === "/home" && location === "/");
                  const Icon = item.icon;
                  const label = t(item.labelKey, item.fallback);
                  const locked = !isExploreFeatureIncludedInPlan(item, effectiveTier);
                  const premiumHint = t("nav.premiumAvailable", "Available with a premium plan");
                  if (locked) {
                    return (
                      <button
                        key={item.path}
                        type="button"
                        aria-label={`${label} — ${premiumHint}`}
                        aria-disabled="true"
                        title={`${label} — ${premiumHint}`}
                        className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-muted-foreground opacity-50 bg-transparent hover:bg-transparent hover:text-muted-foreground focus:bg-transparent cursor-not-allowed ${collapsed ? "justify-center" : ""}`}
                      >
                        {collapsed ? (
                          <span className="relative flex h-4 w-4 flex-shrink-0 items-center justify-center">
                            <Icon className="h-4 w-4" />
                            <LockKeyhole aria-hidden="true" className="absolute -bottom-1.5 -right-2 h-3 w-3 bg-background" />
                          </span>
                        ) : (
                          <>
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="min-w-0 flex-1 break-words text-left leading-tight">{label}</span>
                            <LockKeyhole aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
                          </>
                        )}
                      </button>
                    );
                  }
                  return (
                    <Link key={item.path} href={item.path}>
                      <button
                        className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                        } ${collapsed ? "justify-center" : ""}`}
                         title={collapsed ? label : undefined}
                      >
                         {collapsed ? (
                           <span className="relative flex h-4 w-4 flex-shrink-0 items-center justify-center">
                             <Icon className="h-4 w-4" />
                           </span>
                         ) : (
                           <>
                             <Icon className="h-4 w-4 flex-shrink-0" />
                             <span className="min-w-0 flex-1 break-words text-left leading-tight">{label}</span>
                           </>
                         )}
                      </button>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-border/30 px-2 py-3 space-y-1">
        <Link href="/profile">
          <button
            className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-colors hover:bg-accent/10 ${collapsed ? "justify-center" : ""} ${location === "/profile" ? "bg-primary/10" : ""}`}
            title={collapsed ? (user?.firstName || t("nav.profile", "Profile")) : undefined}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {((user?.firstName?.[0] || "") + (user?.lastName?.[0] || user?.email?.[0]?.toUpperCase() || "U")).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                 <p className="text-sm font-medium text-foreground break-words leading-tight">
                  {user?.firstName || user?.email?.split("@")[0] || t("common.user", "User")}
                </p>
                 <p className="text-[10px] text-muted-foreground break-words leading-tight">
                  {roleLabel || (organization
                    ? `${organization.plan[0].toUpperCase() + organization.plan.slice(1)} organization`
                    : `${getEffectiveTier(user)[0].toUpperCase() + getEffectiveTier(user).slice(1)} personal`)}
                </p>
              </div>
            )}
          </button>
        </Link>
        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? t("nav.logout", "Logout") : undefined}
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>{t("nav.logout", "Logout")}</span>}
        </button>
      </div>
    </aside>
  );
}
